import React, { useMemo } from 'react';
import { CATEGORY_CONFIG } from './RiskCategoryBadge';

/**
 * Get color for score based on risk thresholds
 */
const getScoreColor = (score) => {
  if (score >= 4.0) return CATEGORY_CONFIG.critical.color;
  if (score >= 3.0) return CATEGORY_CONFIG.high.color;
  if (score >= 2.0) return CATEGORY_CONFIG.medium.color;
  return CATEGORY_CONFIG.low.color;
};

/**
 * Simple line chart for risk score trend
 */
const TrendLineChart = ({ scores, height = 120 }) => {
  const chartData = useMemo(() => {
    if (!scores || scores.length === 0) return null;

    // Get scores in chronological order (oldest first)
    const orderedScores = [...scores].reverse();
    const values = orderedScores.map((s) => s.score || 1);

    const minVal = 1;
    const maxVal = 5;
    const padding = 10;
    const chartWidth = 100;
    const chartHeight = height - padding * 2;

    // Generate SVG path points
    const points = values.map((val, idx) => {
      const x = (idx / Math.max(values.length - 1, 1)) * chartWidth;
      const y = chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight + padding;
      return { x, y, score: val, date: orderedScores[idx].calculatedAt };
    });

    // Create smooth path
    const pathD = points.reduce((acc, point, idx) => {
      if (idx === 0) return `M ${point.x} ${point.y}`;
      return `${acc} L ${point.x} ${point.y}`;
    }, '');

    // Create area fill path
    const areaD = `${pathD} L ${points[points.length - 1].x} ${chartHeight + padding} L ${points[0].x} ${chartHeight + padding} Z`;

    return { points, pathD, areaD, orderedScores };
  }, [scores, height]);

  if (!chartData) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        No trend data available
      </div>
    );
  }

  const { points, pathD, areaD, orderedScores } = chartData;
  const latestScore = orderedScores[orderedScores.length - 1]?.score || 0;
  const latestColor = getScoreColor(latestScore);

  return (
    <div className="relative" style={{ height }}>
      <svg viewBox={`0 0 100 ${height}`} className="w-full h-full" preserveAspectRatio="none">
        {/* Grid lines */}
        <line
          x1="0"
          y1={height * 0.25}
          x2="100"
          y2={height * 0.25}
          stroke="#e5e7eb"
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />
        <line
          x1="0"
          y1={height * 0.5}
          x2="100"
          y2={height * 0.5}
          stroke="#e5e7eb"
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />
        <line
          x1="0"
          y1={height * 0.75}
          x2="100"
          y2={height * 0.75}
          stroke="#e5e7eb"
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />

        {/* Risk threshold zones */}
        <rect x="0" y="0" width="100" height={height * 0.25} fill="#fee2e2" opacity="0.3" />
        <rect x="0" y={height * 0.25} width="100" height={height * 0.25} fill="#fed7aa" opacity="0.3" />
        <rect x="0" y={height * 0.5} width="100" height={height * 0.25} fill="#fef9c3" opacity="0.3" />
        <rect x="0" y={height * 0.75} width="100" height={height * 0.25} fill="#dcfce7" opacity="0.3" />

        {/* Area fill */}
        <path d={areaD} fill={latestColor} opacity="0.15" />

        {/* Trend line */}
        <path d={pathD} fill="none" stroke={latestColor} strokeWidth="2" strokeLinecap="round" />

        {/* Data points */}
        {points.map((point, idx) => (
          <circle
            key={idx}
            cx={point.x}
            cy={point.y}
            r="2.5"
            fill="white"
            stroke={getScoreColor(point.score)}
            strokeWidth="1.5"
          />
        ))}
      </svg>

      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-400 pointer-events-none">
        <span>5</span>
        <span>4</span>
        <span>3</span>
        <span>2</span>
        <span>1</span>
      </div>
    </div>
  );
};

/**
 * Statistics summary for trend data
 */
const TrendStatistics = ({ scores }) => {
  const stats = useMemo(() => {
    if (!scores || scores.length === 0) return null;

    const values = scores.map((s) => s.score || 0);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const latest = values[0];
    const oldest = values[values.length - 1];
    const change = latest - oldest;

    return { avg, min, max, latest, change, count: values.length };
  }, [scores]);

  if (!stats) return null;

  return (
    <div className="grid grid-cols-4 gap-2 mt-3">
      <div className="text-center p-2 bg-gray-50 rounded">
        <div className="text-lg font-semibold" style={{ color: getScoreColor(stats.avg) }}>
          {stats.avg.toFixed(1)}
        </div>
        <div className="text-xs text-gray-500">Average</div>
      </div>
      <div className="text-center p-2 bg-gray-50 rounded">
        <div className="text-lg font-semibold text-green-600">{stats.min.toFixed(1)}</div>
        <div className="text-xs text-gray-500">Min</div>
      </div>
      <div className="text-center p-2 bg-gray-50 rounded">
        <div className="text-lg font-semibold text-red-600">{stats.max.toFixed(1)}</div>
        <div className="text-xs text-gray-500">Max</div>
      </div>
      <div className="text-center p-2 bg-gray-50 rounded">
        <div
          className={`text-lg font-semibold ${
            stats.change > 0 ? 'text-red-600' : stats.change < 0 ? 'text-green-600' : 'text-gray-600'
          }`}
        >
          {stats.change > 0 ? '+' : ''}
          {stats.change.toFixed(1)}
        </div>
        <div className="text-xs text-gray-500">Change</div>
      </div>
    </div>
  );
};

/**
 * RiskScoreTrendChart - 30-day trend line chart for risk scores
 */
const RiskScoreTrendChart = ({ history, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4" />
          <div className="h-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const scores = history?.scores || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Risk Score Trend</h3>
        {scores.length > 0 && (
          <span className="text-xs text-gray-500">{scores.length} data points</span>
        )}
      </div>

      {/* Chart */}
      {scores.length > 0 ? (
        <>
          <div className="pl-8">
            <TrendLineChart scores={scores} height={120} />
          </div>
          <TrendStatistics scores={scores} />
        </>
      ) : (
        <div className="text-center py-8 text-gray-500 text-sm">
          <p>No historical data available yet.</p>
          <p className="text-xs mt-1">Risk scores will appear here as they are calculated.</p>
        </div>
      )}
    </div>
  );
};

export default RiskScoreTrendChart;
export { TrendLineChart, TrendStatistics, getScoreColor };
